import { darkTheme } from '../../lib/tokens';
import type { ProviderHealth, Severity, Status } from './types';

interface HealthCardProps {
  data: ProviderHealth;
}

const STATUS_COPY: Record<Status, { label: string; color: string; bg: string }> = {
  up: { label: 'Up', color: darkTheme.colors.success, bg: darkTheme.colors.successBg },
  degraded: { label: 'Degraded', color: darkTheme.colors.warning, bg: darkTheme.colors.warningBg },
  down: { label: 'Down', color: darkTheme.colors.danger, bg: darkTheme.colors.dangerBg },
  unknown: { label: 'Unknown', color: darkTheme.colors.textTertiary, bg: darkTheme.colors.bgElevated },
};

const SEVERITY_COLOR: Record<Severity, string> = {
  ok: darkTheme.colors.success,
  warn: darkTheme.colors.warning,
  crit: darkTheme.colors.danger,
};

export function HealthCard({ data }: HealthCardProps): JSX.Element {
  const statusInfo = STATUS_COPY[data.status];
  const isError = data.status === 'down';

  return (
    <article
      className="flex flex-col rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: isError
          ? `${darkTheme.colors.danger}4d`
          : darkTheme.colors.borderSubtle,
        minHeight: 220,
      }}
    >
      <header className="mb-3 flex items-start justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-text-primary">
          {data.displayName}
          {data.dashboardUrl && (
            <a
              href={data.dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              title={`${data.displayName} im Anbieter-Dashboard öffnen`}
              className="text-text-tertiary transition-colors hover:text-accent-light"
              onClick={(e) => e.stopPropagation()}
              aria-label={`${data.displayName} extern öffnen`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="11"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
            </a>
          )}
        </h3>
        <span
          className="rounded-full px-2 py-0.5 text-[11px] font-semibold"
          style={{ background: statusInfo.bg, color: statusInfo.color }}
        >
          ● {statusInfo.label}
        </span>
      </header>

      <ul className="flex-1 space-y-1.5">
        {data.metrics.length === 0 && (
          <li className="text-xs text-text-tertiary">Keine Metriken</li>
        )}
        {data.metrics.map((m) => (
          <li key={m.label} className="flex items-baseline justify-between gap-2 text-xs">
            <span className="text-text-secondary">{m.label}</span>
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: SEVERITY_COLOR[m.severity] }}
              />
              <span
                className="tabular font-medium text-text-primary"
                title={m.hint}
              >
                {m.value}
                {m.unit ? ` ${m.unit}` : ''}
              </span>
            </span>
          </li>
        ))}
      </ul>

      {data.error && (
        <p
          className="mt-3 rounded-sm font-mono text-[10px]"
          style={{ background: darkTheme.colors.dangerBg, color: darkTheme.colors.danger, padding: '6px 8px' }}
        >
          {data.error}
        </p>
      )}

      <footer className="mt-3 flex items-center justify-between border-t pt-2 text-[10px] text-text-tertiary"
        style={{ borderColor: darkTheme.colors.borderSubtle }}>
        <span>Letzter Check: {formatRelative(data.lastCheckedAt)}</span>
        {data.latencyMs != null && <span>{data.latencyMs} ms</span>}
      </footer>
    </article>
  );
}

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return `vor ${Math.floor(ms / 1000)} s`;
  if (ms < 3_600_000) return `vor ${Math.floor(ms / 60_000)} min`;
  return `vor ${Math.floor(ms / 3_600_000)} h`;
}
